import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { LocaleField } from '@/components/forms/LocaleField';
import { cn } from '@/lib/cn';
import { placeholderSet, runeLength } from '@/lib/copyValidation';
import { COPY_LOCALES, type CopyLocale } from '@/schemas/admin/copy';

export const LOCALE_DIR: Record<CopyLocale, 'ltr' | 'rtl'> = {
  en: 'ltr',
  ar: 'rtl',
  'eg-ar': 'rtl',
};

/** A textarea, not an input, once the shipped English needs more than one line. */
export function preferMultiline(baseline: string | undefined | null): boolean {
  if (!baseline) return false;
  return baseline.includes('\n') || runeLength(baseline) > 80;
}

interface StringEditorProps {
  id: string;
  /** The full tri-locale draft. Every locale is editable in one field. */
  values: Record<CopyLocale, string>;
  onChange: (locale: CopyLocale, next: string) => void;
  activeLocale: CopyLocale;
  onActiveLocaleChange: (locale: CopyLocale) => void;
  baseline: Partial<Record<CopyLocale, string>>;
  maxLen: number | null;
  placeholders: readonly string[];
  errors?: Partial<Record<CopyLocale, string>>;
  label: React.ReactNode;
}

/**
 * The `string` shape — 593 of the catalog's 624 keys.
 *
 * All three locales live in one `LocaleField` (AC4); the drawer owns which one
 * is showing, so the locale strip with its status chips is the single
 * switcher rather than a second tab row inside the field.
 */
export function StringEditor({
  id,
  values,
  onChange,
  activeLocale,
  onActiveLocaleChange,
  baseline,
  maxLen,
  placeholders,
  errors = {},
  label,
}: StringEditorProps) {
  const { t } = useTranslation('copy');

  const activeBaseline = baseline[activeLocale] ?? '';
  const current = values[activeLocale] ?? '';
  const limit = maxLen && maxLen > 0 ? maxLen : null;
  const used = runeLength(current);
  const over = limit != null && used > limit;

  const present = React.useMemo(() => placeholderSet(current), [current]);

  return (
    <div>
      <LocaleField
        id={id}
        label={label}
        locales={COPY_LOCALES}
        activeLocale={activeLocale}
        onActiveLocaleChange={(loc) => onActiveLocaleChange(loc)}
        renderTabs={false}
        required={COPY_LOCALES}
        value={{
          en: values.en ?? '',
          ar: values.ar ?? '',
          'eg-ar': values['eg-ar'] ?? '',
        }}
        onChange={(next) => onChange(activeLocale, next[activeLocale] ?? '')}
        multiline={preferMultiline(activeBaseline) || preferMultiline(current)}
        rows={4}
        errors={errors}
        hint={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {limit != null ? (
              <span className={cn(over && 'font-semibold text-[color:var(--color-danger)]')}>
                {t('editor.charCount', { used, limit })}
              </span>
            ) : (
              <span>{t('editor.charCountNoLimit', { used })}</span>
            )}
            {placeholders.map((token) => {
              const ok = present.has(token);
              return (
                <span
                  key={token}
                  dir="ltr"
                  className={cn(
                    'inline-flex items-center gap-1 rounded-[var(--radius-pill)] border px-2 py-0.5 font-mono text-[11px]',
                    ok
                      ? 'border-[color:var(--color-success)] text-[color:var(--color-success)]'
                      : 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]'
                  )}
                  title={ok ? t('editor.placeholderPresent') : t('editor.placeholderMissing')}
                >
                  <span aria-hidden>{ok ? '✓' : '!'}</span>
                  {`{${token}}`}
                  <span className="sr-only">
                    {ok ? t('editor.placeholderPresent') : t('editor.placeholderMissing')}
                  </span>
                </span>
              );
            })}
          </span>
        }
      />
      <BaselineReference locale={activeLocale} value={activeBaseline} />
    </div>
  );
}

/** The shipped value for the locale on screen — always visible while editing. */
export function BaselineReference({
  locale,
  value,
  className,
}: {
  locale: CopyLocale;
  value: string;
  className?: string;
}) {
  const { t } = useTranslation('copy');
  return (
    <div
      className={cn(
        'rounded-[var(--radius-md)] border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] p-3',
        className
      )}
    >
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        {t('editor.baselineLabel')}
      </p>
      {value ? (
        <p dir={LOCALE_DIR[locale]} className="whitespace-pre-wrap text-sm text-[color:var(--color-fg-muted)]">
          {value}
        </p>
      ) : (
        <p className="text-sm italic text-[color:var(--color-fg-muted)]">{t('editor.baselineEmpty')}</p>
      )}
    </div>
  );
}
